import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ThemeEditor } from '@/components/ThemeEditor';
import { ThemePreview } from '@/components/ThemePreview';
import { CustomFieldEditor } from '@/components/CustomFieldEditor';
import { ImageUpload } from '@/components/ImageUpload';
import { DraggableGallery } from '@/components/DraggableGallery';
import { useEvents } from '@/hooks/useEvents';
import { ThemeConfig, CustomField } from '@/types';

export function EditEvent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentEvent, getEvent, updateEvent, loading, error } = useEvents();

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    date: '',
    location: '',
    description: '',
  });
  const [theme, setTheme] = useState<ThemeConfig | null>(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [coverPhoto, setCoverPhoto] = useState<string>('');
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);

  useEffect(() => {
    if (id) getEvent(id);
  }, [id, getEvent]);

  useEffect(() => {
    if (currentEvent) {
      setFormData({
        name: currentEvent.name,
        slug: currentEvent.slug,
        date: currentEvent.date,
        location: currentEvent.location,
        description: currentEvent.description || '',
      });
      setTheme(currentEvent.theme);
      setCustomFields(currentEvent.custom_fields || []);
      setCoverPhoto(currentEvent.cover_photo_url || '');
      setGalleryUrls(currentEvent.gallery_urls || []);
    }
  }, [currentEvent]);

  const handleSave = async () => {
    if (!id) return;
    const result = await updateEvent(id, {
      ...formData,
      theme: theme || undefined,
      custom_fields: customFields,
      cover_photo_url: coverPhoto || undefined,
      gallery_urls: galleryUrls.length > 0 ? galleryUrls : undefined,
    });
    if (result) navigate(`/events/${id}/admin`);
  };

  if (loading && !currentEvent) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-display">Edit Event</h1>
          <p className="text-gray-500 mt-1">Update your event details</p>
        </div>

        <div className="space-y-8">
          {/* Basic Details */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Event Details</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input-field" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                <input type="datetime-local" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="input-field" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input-field min-h-[120px] resize-y" rows={4} />
            </div>
          </section>

          {/* Cover Photo */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Cover Photo</h2>
            {coverPhoto && (
              <div className="relative rounded-lg overflow-hidden">
                <img src={coverPhoto} alt="Cover" className="w-full h-48 object-cover" />
                <button onClick={() => setCoverPhoto('')} className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            )}
            {id && <ImageUpload eventId={id} onUpload={setCoverPhoto} label="Upload cover photo" />}
          </section>

          {/* Gallery */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Gallery</h2>
            <DraggableGallery urls={galleryUrls} onChange={setGalleryUrls} />
            {id && <ImageUpload eventId={id} onUpload={(url) => setGalleryUrls([...galleryUrls, url])} label="Add gallery photos" />}
          </section>

          {/* Theme */}
          {theme && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Theme</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ThemeEditor theme={theme} onChange={setTheme} />
                <ThemePreview theme={theme} />
              </div>
            </section>
          )}

          {/* Custom Fields */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">RSVP Fields</h2>
            <CustomFieldEditor fields={customFields} onChange={setCustomFields} />
          </section>

          {error && <div className="p-4 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

          <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
            <button onClick={handleSave} disabled={loading} className="btn-primary disabled:opacity-50">{loading ? 'Saving...' : 'Save Changes'}</button>
            <button onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
